<template>
  <div class="administration-page eh-ds">
    <header class="admin-header">
      <h1 class="admin-title">
        Management and Administration
        <help-icon help-id="administration" title="Help for Administration" />
      </h1>
    </header>

    <div class="admin-menu-layout">
      <nav class="admin-menu" aria-label="Administration menu">
        <button
          type="button"
          class="admin-menu-item admin-menu-item--accent"
          @click="openAddEditUsersChooser"
        >
          <span class="admin-menu-item__body">
            <span class="admin-menu-item__title">Add/Edit Users</span>
            <span class="admin-menu-item__hint">Register a new account or update an existing one</span>
          </span>
          <span class="admin-menu-item__arrow" aria-hidden="true">›</span>
        </button>

        <button type="button" class="admin-menu-item" @click="openGroups">
          <span class="admin-menu-item__body">
            <span class="admin-menu-item__title">Manage Groups</span>
            <span class="admin-menu-item__hint">Organizational groups and subgroups</span>
          </span>
          <span class="admin-menu-item__arrow" aria-hidden="true">›</span>
        </button>

        <button type="button" class="admin-menu-item" @click="openPrivileges">
          <span class="admin-menu-item__body">
            <span class="admin-menu-item__title">{{
              canAdjustPermissions ? "Manage Privileges" : "My Privileges"
            }}</span>
            <span class="admin-menu-item__hint">{{
              canAdjustPermissions
                ? "Access rules for groups and users"
                : "View your own access rights (read-only)"
            }}</span>
          </span>
          <span class="admin-menu-item__arrow" aria-hidden="true">›</span>
        </button>

        <!-- Configuration → flyout: Users and Status, Directive Re-Authorization Rules, Customization -->
        <div
          class="admin-menu-flyout"
          :class="{ 'admin-menu-flyout--open': openSubmenu === 'configuration' }"
        >
          <button
            type="button"
            class="admin-menu-item"
            :aria-expanded="openSubmenu === 'configuration' ? 'true' : 'false'"
            @click="toggleSubmenu('configuration')"
          >
            <span class="admin-menu-item__body">
              <span class="admin-menu-item__title">Configuration</span>
              <span class="admin-menu-item__hint">Organization setup and app settings</span>
            </span>
            <span class="admin-menu-item__arrow" aria-hidden="true">›</span>
          </button>
          <div
            v-if="openSubmenu === 'configuration'"
            class="admin-submenu"
            role="menu"
            aria-label="Configuration"
          >
            <button
              type="button"
              class="admin-submenu-item"
              role="menuitem"
              @click="openMyOrg"
            >
              Users and Status
            </button>
            <button
              type="button"
              class="admin-submenu-item"
              role="menuitem"
              @click="openDirectivePolicy"
            >
              Directive Re-Authorization Rules
            </button>
            <button
              type="button"
              class="admin-submenu-item"
              role="menuitem"
              @click="openCustomization"
            >
              Customization
            </button>
            <!-- Alert triggers, security contacts, open-alert review. -->
            <button
              v-if="canOpenSecuritySettings"
              type="button"
              class="admin-submenu-item"
              role="menuitem"
              @click="openSecuritySettings"
            >
              Security settings
            </button>
          </div>
        </div>

        <!-- Auditing and Reports → flyout: Audit Log, Privilege Reports -->
        <div
          class="admin-menu-flyout"
          :class="{ 'admin-menu-flyout--open': openSubmenu === 'auditing' }"
        >
          <button
            type="button"
            class="admin-menu-item"
            :aria-expanded="openSubmenu === 'auditing' ? 'true' : 'false'"
            @click="toggleSubmenu('auditing')"
          >
            <span class="admin-menu-item__body">
              <span class="admin-menu-item__title">Auditing and Reports</span>
              <span class="admin-menu-item__hint">Activity history and privilege exports</span>
            </span>
            <span class="admin-menu-item__arrow" aria-hidden="true">›</span>
          </button>
          <div
            v-if="openSubmenu === 'auditing'"
            class="admin-submenu"
            role="menu"
            aria-label="Auditing and Reports"
          >
            <button
              type="button"
              class="admin-submenu-item"
              role="menuitem"
              @click="openAudits"
            >
              Audit Log
            </button>
            <button
              type="button"
              class="admin-submenu-item"
              role="menuitem"
              @click="openPrivilegeReports"
            >
              Privilege Reports
            </button>
          </div>
        </div>

        <button type="button" class="admin-menu-item" @click="openBreakGlass">
          <span class="admin-menu-item__body">
            <span class="admin-menu-item__title">Break-glass</span>
            <span class="admin-menu-item__hint">Temporarily elevate caregiver privileges</span>
          </span>
          <span class="admin-menu-item__arrow" aria-hidden="true">›</span>
        </button>

        <!-- Trello nRhCnuT0: platform operators only, never offered in a customer organization. -->
        <button
          v-if="canOpenPlatformAdministration"
          type="button"
          class="admin-menu-item admin-menu-item--platform"
          @click="openPlatformAdministration"
        >
          <span class="admin-menu-item__body">
            <span class="admin-menu-item__title">Platform Administration</span>
            <span class="admin-menu-item__hint">Customer organizations and onboarding</span>
          </span>
          <span class="admin-menu-item__arrow" aria-hidden="true">›</span>
        </button>
      </nav>
    </div>

    <!-- Add/Edit Users chooser (wireframe: add new user + edit via combobox) -->
    <Teleport to="body">
      <div
        v-if="showAddEditChooser"
        class="aeu-overlay eh-popup-overlay"
        @click.self="closeAddEditChooser"
      >
        <div
          class="aeu-dialog eh-popup-panel eh-ds"
          role="dialog"
          aria-modal="true"
          aria-labelledby="aeu-dialog-title"
        >
          <div class="aeu-dialog-head">
            <h2 id="aeu-dialog-title" class="aeu-dialog-title">Add / Edit User</h2>
            <help-icon help-id="adduser" title="Help for Add / Edit User" />
            <button
              type="button"
              class="aeu-close"
              aria-label="Close"
              @click="closeAddEditChooser"
            >
              &times;
            </button>
          </div>

          <div class="aeu-dialog-body">
            <button type="button" class="btn btn-primary aeu-add-btn" @click="startAddUser">
              Add New User
            </button>

            <p class="aeu-or-label">or edit existing user</p>

            <div class="aeu-combo-wrap">
              <input
                v-model="editQuery"
                type="text"
                class="aeu-combo-input"
                role="combobox"
                :aria-expanded="editDropdownOpen ? 'true' : 'false'"
                aria-autocomplete="list"
                aria-controls="aeu-combo-list"
                placeholder="Search or select a user…"
                autocomplete="off"
                @input="onEditQueryInput"
                @focus="openEditDropdown"
                @keydown.escape="editDropdownOpen = false"
              />
              <button
                type="button"
                class="aeu-combo-caret"
                tabindex="-1"
                aria-label="Show users"
                @mousedown.prevent="toggleEditDropdown"
              >
                ▾
              </button>
              <ul
                v-if="editDropdownOpen"
                id="aeu-combo-list"
                class="aeu-combo-list"
                role="listbox"
              >
                <li v-if="editSearching" class="aeu-combo-empty">Searching…</li>
                <template v-else>
                  <li
                    v-for="r in editResults"
                    :key="r.uniqueID || r.email"
                    role="option"
                  >
                    <button
                      type="button"
                      class="aeu-combo-option"
                      @click="pickEditUser(r)"
                    >
                      <span class="aeu-avatar">{{ editInitials(r) }}</span>
                      <span class="aeu-combo-text">
                        <span class="aeu-combo-name"
                          >{{ r.firstname }} {{ r.lastname }}</span
                        >
                        <span class="aeu-combo-sub"
                          >{{ r.accountType || "Account" }} · {{ r.email }}</span
                        >
                      </span>
                    </button>
                  </li>
                  <li v-if="editResults.length === 0" class="aeu-combo-empty">
                    No matches found
                  </li>
                </template>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </Teleport>

    <div class="col-sm-12 col-md-12 col-lg-12 addNurseSec">
      <add-user-form v-if="showAddUserModal" @close="closeAddUserModal" />
      <edit-user-search
        v-if="showEditUserModal"
        :initial-record="editUserRecord"
        @close="closeEditUserModal"
      />
    </div>
  </div>
</template>

<script>
import axios from "axios";
import AddUserForm from "@/components/private-layouts/welcome/AddUserForm.vue";
import EditUserSearch from "@/components/private-layouts/welcome/EditUserSearch.vue";
import HelpIcon from "@/components/help/HelpIcon.vue";
import { mapGetters } from "vuex";
import { CURR_API } from "@/constants/apis";
import { usePrivilegesStore } from "@/stores/privileges";
import { useSessionStore } from "@/stores/session";
import { PRIVILEGES } from "@/constants/privileges";

export default {
  name: "AdministrationPage",
  components: {
    AddUserForm,
    EditUserSearch,
    HelpIcon,
  },
  data() {
    return {
      openSubmenu: null,
      showAddEditChooser: false,
      showAddUserModal: false,
      showEditUserModal: false,
      editUserRecord: null,
      editQuery: "",
      editResults: [],
      editSearching: false,
      editDropdownOpen: false,
      editDebounceTimer: null,
    };
  },
  computed: {
    ...mapGetters("login", {
      allData: "getAllData",
    }),
    canCreateAccounts() {
      return usePrivilegesStore().effectiveGranted(PRIVILEGES.CREATE_ACCOUNTS);
    },
    canAdjustPermissions() {
      return usePrivilegesStore().effectiveGranted(PRIVILEGES.ADJUST_PERMISSIONS);
    },
    canViewPrivilegeReports() {
      return usePrivilegesStore().effectiveGranted(PRIVILEGES.GENERATE_PRIVILEGES);
    },
    canViewAudits() {
      return usePrivilegesStore().effectiveGranted(PRIVILEGES.VIEW_AUDITS);
    },
    canManageDirectivePolicy() {
      return usePrivilegesStore().effectiveGranted(PRIVILEGES.DIRECTIVE_POLICY_MANAGE);
    },
    canAssignNonPatientsToGroups() {
      return usePrivilegesStore().effectiveGranted(PRIVILEGES.ASSIGN_NON_PATIENTS_TO_GROUPS);
    },
    canViewMyOrg() {
      return usePrivilegesStore().effectiveGranted(PRIVILEGES.MY_ORG_VIEW);
    },
    /**
     * Security settings (Trello ybyVP1vB, wh5sgQgT): the triggers and contacts
     * need org:admin (or a platform operator's platform:configure), the alert
     * review audit:read; the page shows each section to whoever may use it.
     */
    canOpenSecuritySettings() {
      const session = useSessionStore();
      return session.can("org:admin") || session.can("audit:read")
        || (Boolean(session.me?.is_platform_operator) && session.can("platform:configure"));
    },
    /**
     * Platform Administration (Trello nRhCnuT0): an operator of the platform
     * tenant who holds `platform:admin`. Both come from /me; the permission is
     * platform-only, so no one in a customer organization can hold it, and the
     * API decides every call regardless.
     */
    canOpenPlatformAdministration() {
      const session = useSessionStore();
      return Boolean(session.me?.is_platform_operator) && session.can(PRIVILEGES.PLATFORM_ADMIN);
    },
  },
  beforeUnmount() {
    if (this.editDebounceTimer) clearTimeout(this.editDebounceTimer);
  },
  methods: {
    /** Open or close a flyout submenu (configuration / auditing). */
    toggleSubmenu(key) {
      this.openSubmenu = this.openSubmenu === key ? null : key;
    },
    /** Privilege-gated entry to the Add / Edit User chooser dialog. */
    openAddEditUsersChooser() {
      if (!this.canCreateAccounts) {
        alert("You do not have permission to create or edit accounts.");
        return;
      }
      this.openSubmenu = null;
      this.showAddEditChooser = true;
      this.editQuery = "";
      this.editResults = [];
      this.editDropdownOpen = false;
    },
    /** Dismiss the Add / Edit User chooser and clear combobox state. */
    closeAddEditChooser() {
      this.showAddEditChooser = false;
      this.editQuery = "";
      this.editResults = [];
      this.editDropdownOpen = false;
    },
    /** Close the chooser and open the existing Add User form. */
    startAddUser() {
      this.closeAddEditChooser();
      this.showAddUserModal = true;
    },
    /** Show the edit-user combobox list and load (or refresh) results. */
    openEditDropdown() {
      this.editDropdownOpen = true;
      this.runEditSearch(this.editQuery.trim());
    },
    /** Caret control: open the full list, or close if already open. */
    toggleEditDropdown() {
      if (this.editDropdownOpen) {
        this.editDropdownOpen = false;
        return;
      }
      this.openEditDropdown();
    },
    /** Debounced type-ahead: keep the list open and re-filter as the user types. */
    onEditQueryInput() {
      clearTimeout(this.editDebounceTimer);
      this.editDropdownOpen = true;
      const term = this.editQuery.trim();
      this.editDebounceTimer = setTimeout(() => this.runEditSearch(term), 300);
    },
    /**
     * Fetch editable account users for the combobox.
     * Empty term uses "%" because the webhook returns [] for a blank searchTerm.
     */
    async runEditSearch(term) {
      this.editSearching = true;
      try {
        const searchTerm = term || "%";
        const res = await axios.post(
          CURR_API + "/tswebhook.php",
          {
            AccountUser: {
              API_Meth: "SearchAccountForEdit",
              accountnumber: this.allData.patient.externId2,
              searchTerm,
            },
          },
          { headers: { "Content-Type": "application/json;" } }
        );
        this.editResults = Array.isArray(res?.data?.records) ? res.data.records : [];
      } catch (err) {
        console.error("User search failed:", err);
        this.editResults = [];
      } finally {
        this.editSearching = false;
      }
    },
    /** Choose a combobox result and open EditUserSearch with that record. */
    pickEditUser(record) {
      this.closeAddEditChooser();
      this.editUserRecord = record;
      this.showEditUserModal = true;
    },
    /** Avatar initials for a search result row. */
    editInitials(r) {
      const f = (r.firstname || "").trim().charAt(0);
      const l = (r.lastname || "").trim().charAt(0);
      return ((f + l) || "?").toUpperCase();
    },
    /** Navigate to Users and Status when MY_ORG_VIEW is granted. */
    openMyOrg() {
      if (!this.canViewMyOrg) {
        alert("You do not have permission to view Users and Status.");
        return;
      }
      this.$router.push("/myOrg");
    },
    openGroups() {
      if (!this.canAssignNonPatientsToGroups) {
        alert("You do not have permission to adjust groups.");
        return;
      }
      this.$router.push("/groups");
    },
    openPrivileges() {
      // ADJUST_PERMISSIONS unlocks the full editor; without it the page is
      // still reachable as a read-only view of the caller's own privileges.
      this.$router.push("/privileges");
    },
    openPrivilegeReports() {
      if (!this.canViewPrivilegeReports) {
        alert("You do not have permission to view privilege reports.");
        return;
      }
      this.$router.push("/privilege-reports");
    },
    openAudits() {
      if (!this.canViewAudits) {
        alert("You do not have permission to audit data.");
        return;
      }
      this.$router.push("/audit");
    },
    openDirectivePolicy() {
      if (!this.canManageDirectivePolicy) {
        alert("You do not have permission to manage directive re-authorization rules.");
        return;
      }
      this.$router.push("/directive-reauth-rules");
    },
    openBreakGlass() {
      if (!this.canAdjustPermissions) {
        alert("You do not have permission to manage break-glass.");
        return;
      }
      this.$router.push("/break-glass");
    },
    openCustomization() {
      if (!this.canAdjustPermissions) {
        alert("You do not have permission to manage customization.");
        return;
      }
      this.$router.push("/customization");
    },
    openSecuritySettings() {
      this.openSubmenu = null;
      this.$router.push("/security-settings");
    },
    openPlatformAdministration() {
      this.openSubmenu = null;
      this.$router.push("/platform");
    },
    closeAddUserModal() {
      this.showAddUserModal = false;
    },
    /** Close edit flow and clear any preselected record from the chooser. */
    closeEditUserModal() {
      this.showEditUserModal = false;
      this.editUserRecord = null;
    },
  },
};
</script>

<style scoped lang="scss">
$admin-navy: #173a63;
$admin-border: #e2e8f0;
$admin-muted: #64748b;

.administration-page {
  /* Match the button column width; flyouts are absolutely positioned and
     must not widen or shift this centered block. */
  max-width: 36rem;
  margin: 0 auto;
  padding: 1.5rem 1.25rem 2.5rem;
  overflow: visible;
}

.admin-header {
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid $admin-border;
  text-align: center;
}

.admin-title {
  margin: 0;
  font-size: 1.65rem;
  font-weight: 700;
  color: $admin-navy;
  letter-spacing: -0.02em;
}

.admin-menu-layout {
  width: 100%;
  margin: 0 auto;
}

.admin-menu {
  display: flex;
  flex-direction: column;
  gap: 0.65rem;
}

.admin-menu-flyout {
  position: relative;
}

.admin-menu-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  width: 100%;
  text-align: left;
  padding: 0.95rem 1.1rem;
  border: 1px solid $admin-border;
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.06);
  cursor: pointer;
  transition:
    border-color 0.15s ease,
    box-shadow 0.15s ease,
    background 0.15s ease;

  &:hover {
    border-color: #c5d0e0;
    box-shadow: 0 4px 14px rgba(15, 23, 42, 0.08);
    background: #fafbff;
  }

  &:focus-visible {
    outline: 2px solid #5a7ab0;
    outline-offset: 2px;
  }

  &--accent {
    border-color: #c5d0e8;
    background: linear-gradient(135deg, #f8faff 0%, #fff 55%);

    &:hover {
      border-color: #a8c4f0;
      background: linear-gradient(135deg, #f0f6ff 0%, #fafbff 100%);
    }
  }
}

.admin-menu-flyout--open > .admin-menu-item {
  border-color: #a8c4f0;
  background: #f4f8ff;
}

.admin-menu-item__body {
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  min-width: 0;
}

.admin-menu-item__title {
  font-size: 1.05rem;
  font-weight: 600;
  color: $admin-navy;
}

.admin-menu-item__hint {
  font-size: 0.82rem;
  color: $admin-muted;
  line-height: 1.35;
}

.admin-menu-item__arrow {
  flex-shrink: 0;
  font-size: 1.35rem;
  font-weight: 300;
  color: #7f96b8;
  line-height: 1;
}

.admin-submenu {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  margin-top: 0.45rem;
  padding: 0.55rem;
  border: 1px solid $admin-border;
  border-radius: 10px;
  background: #fff;
  box-shadow: 0 4px 16px rgba(15, 23, 42, 0.08);
}

@media (min-width: 900px) {
  .admin-submenu {
    position: absolute;
    left: calc(100% + 0.65rem);
    top: 0;
    margin-top: 0;
    width: max-content;
    min-width: 15.5rem;
    max-width: 20rem;
    z-index: 5;
    /* Out of document flow — does not affect centering of the button column. */
  }
}

.admin-submenu-item {
  display: block;
  width: 100%;
  text-align: left;
  padding: 0.7rem 0.85rem;
  border: 1px solid transparent;
  border-radius: 8px;
  background: #f8fafc;
  color: $admin-navy;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;

  &:hover {
    background: #eef4ff;
    border-color: #d0dcf0;
  }

  &:focus-visible {
    outline: 2px solid #5a7ab0;
    outline-offset: 1px;
  }
}

/* —— Add / Edit User chooser (aligned with EditUserSearch modal) —— */
.aeu-overlay {
  /* Backdrop color + z-index from brand-ds (.eh-popup-overlay / --eh-popup-*). */
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow-y: auto;
  padding: 24px 12px;
}

.aeu-dialog {
  /* Surface, border, radius from brand-ds (.eh-popup-panel / --eh-popup-*). */
  width: 100%;
  max-width: 480px;
  padding: 1.5rem;
}

.aeu-dialog-head {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
}

.aeu-dialog-title {
  margin: 0;
  flex: 1;
  font-size: 1.25rem;
  font-weight: 700;
  color: $admin-navy;
  letter-spacing: -0.01em;
}

.aeu-close {
  border: none;
  background: transparent;
  font-size: 1.5rem;
  line-height: 1;
  color: #64748b;
  cursor: pointer;
  padding: 0 0.15rem;

  &:hover {
    color: #1f2937;
  }
}

.aeu-dialog-body {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 0.85rem;
}

.aeu-add-btn {
  align-self: flex-start;
}

.aeu-or-label {
  margin: 0.25rem 0 0;
  font-size: 0.9rem;
  color: #64748b;
}

.aeu-combo-wrap {
  position: relative;
}

.aeu-combo-input {
  width: 100%;
  box-sizing: border-box;
  padding: 8px 2rem 8px 10px;
  border: 1px solid #cbd5e1;
  border-radius: 8px;
  font-size: 14px;
  background: #fff;

  &:focus {
    outline: none;
    border-color: #5a7ab0;
    box-shadow: 0 0 0 3px rgba(90, 122, 176, 0.15);
  }
}

.aeu-combo-caret {
  position: absolute;
  right: 0.25rem;
  top: 50%;
  transform: translateY(-50%);
  border: none;
  background: transparent;
  color: #64748b;
  font-size: 0.85rem;
  line-height: 1;
  padding: 0.35rem 0.45rem;
  cursor: pointer;

  &:hover {
    color: #1f2937;
  }
}

.aeu-combo-list {
  position: absolute;
  left: 0;
  right: 0;
  top: calc(100% + 4px);
  z-index: 2;
  max-height: 280px;
  overflow-y: auto;
  margin: 0;
  padding: 4px;
  list-style: none;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.14);
}

.aeu-combo-option {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  text-align: left;
  padding: 8px;
  border: none;
  border-radius: 6px;
  background: transparent;
  cursor: pointer;

  &:hover {
    background: #f1f5f9;
  }
}

.aeu-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  background: #5a7ab0;
}

.aeu-combo-text {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.aeu-combo-name {
  font-size: 13.5px;
  font-weight: 600;
  color: #1f2937;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.aeu-combo-sub {
  font-size: 11.5px;
  color: #64748b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.aeu-combo-empty {
  padding: 10px;
  font-size: 12.5px;
  color: #64748b;
  text-align: center;
}
</style>
